import React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Divider } from "@mui/material";
import EditOutlined from "@mui/icons-material/EditOutlined";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import TicketHistoryTab from "./ticketComponents/TicketHistoryTab";
import TicketResponsesTab from "./ticketComponents/TicketResponsesTab";
import RowsSkeleton from "components/Shared/Skeleton/RowsSkeleton";
import StagePathway from "components/Shared/StagePathway";
import TicketBasicDetail from "./ticketComponents/TicketBasicDetail";
import CustomTabs from "components/Shared/CustomTabs";
import CustomTabPanel from "components/Shared/CustomTabPanel";
import EnableDisableButton from "components/Shared/EnableDisableButton";

import {
  useGetTicketDetailQuery,
  useUpdateTicketMutation,
} from "store/services/ticketApi";

import { useAuth } from "hooks/useAuth";
import { useAppMeta } from "hooks";
import {
  TicketDetailContext,
  useTicketDetailContext,
} from "contexts/TicketDetailContext";

const TicketDetailScreen = () => {
  const { id } = useParams();

  const { isAdmin, isVendor, isVendorStaff } = useAuth();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "responses";

  const breadcrumbs = [
    ...(isAdmin
      ? [
        {
          title: "Tickets",
          path: searchParams.get("prevTab")
            ? `/app/crm/ticket/list?tab=${searchParams.get("prevTab")}`
            : "/app/crm/ticket/list",
        },
      ]
      : isVendor
        ? [
          {
            title: "Tickets",
            path: searchParams.get("prevTab")
              ? `/app/vendor/ticket/list?tab=${searchParams.get("prevTab")}`
              : "/app/vendor/ticket/list",
          },
        ]
        : isVendorStaff
          ? [
            {
              title: "Tickets",
              path: searchParams.get("prevTab")
                ? `/app/staff/ticket/list?tab=${searchParams.get("prevTab")}`
                : "/app/staff/ticket/list",
            },
          ]
          : []),
    { title: "Ticket Detail" },
  ];

  const ticketStages = ["Open", "In Progress", "Closed"];

  const ticketDetailTabs = [
    {
      label: "Responses",
      value: "responses",
    },
    {
      label: "History",
      value: "history",
    },
  ];

  // RTK Query
  const {
    data: ticketDetail,
    isLoading: loadingTicket,
    error,
  } = useGetTicketDetailQuery(id);

  const [updateTicket, { isLoading: updatingTicket }] =
    useUpdateTicketMutation();

  // Check if ticket is closed
  const isTicketClosed = ticketDetail?.stage === "Closed";

  // methods
  const handleChangeTicketStage = async (stage) => {
    // Prevent changing stage if ticket is closed
    if (isTicketClosed) {
      toast.warning("Cannot change stage of a closed ticket!");
      return;
    }

    try {
      await updateTicket({
        _id: ticketDetail?._id,
        stage,
        ticketComment: `Changed stage from "${ticketDetail?.stage}" to "${stage}"`,
      }).unwrap();
      toast.info("Ticket's stage updated!");
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't update ticket's stage!");
    }
  };

  useAppMeta(
    !ticketDetail ? "Ticket Detail" : `Ticket - ${ticketDetail?.subject}`
  );

  return (
    <TicketDetailContext.Provider value={{ ticketDetail, loadingTicket }}>
      <BreadcrumbLayout
        breadcrumbs={breadcrumbs}
        isBusy={loadingTicket || updatingTicket}
        headerActions={<PageActions isVendor={isVendor} />}
      >
        <BreadcrumbLayout.Error error={error} />

        {isAdmin && !!ticketDetail?.stage && (
          <StagePathway
            stages={ticketStages}
            activeStage={ticketDetail?.stage}
            getStageLabel={(stage) => stage}
            getStageValue={(stage) => stage}
            onChange={handleChangeTicketStage}
            disabled={isTicketClosed}
          />
        )}

        <BreadcrumbLayout.Paper>
          <TicketBasicDetail />

          <Divider />

          <CustomTabs tabs={ticketDetailTabs} activeTab={activeTab} />

          {!loadingTicket ? (
            <>
              <CustomTabPanel activeTab={activeTab} value="responses">
                <TicketResponsesTab />
              </CustomTabPanel>

              <CustomTabPanel activeTab={activeTab} value="history">
                <TicketHistoryTab />
              </CustomTabPanel>
            </>
          ) : (
            <RowsSkeleton />
          )}
        </BreadcrumbLayout.Paper>
      </BreadcrumbLayout>
    </TicketDetailContext.Provider>
  );
};

export default TicketDetailScreen;

const PageActions = ({ isVendor }) => {
  const { isAdmin } = useAuth();

  const { ticketDetail } = useTicketDetailContext();

  // RTK Query
  const [updateTicket, { isLoading: updatingTicket }] =
    useUpdateTicketMutation();

  // methods
  const handleUpdateStatus = async () => {
    try {
      await updateTicket({
        _id: ticketDetail?._id,
        isActive: !ticketDetail?.isActive,
        ticketComment: `Changed the status to "${ticketDetail?.isActive ? "Inactive" : "Active"
          }"`,
      });
      toast.info(
        `Transaction successfully ${ticketDetail?.isActive ? "Disabled" : "Enabled"
        }!`
      );
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't update ticket's status!");
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="flex space-x-4">
      <EnableDisableButton
        suffix="Ticket"
        isActive={ticketDetail?.isActive}
        loading={updatingTicket}
        onClick={handleUpdateStatus}
      />

      <Link
        to={
          isVendor
            ? `/app/vendor/ticket/${ticketDetail?._id}/edit`
            : `/app/crm/ticket/${ticketDetail?._id}/edit`
        }
      >
        <Button variant="contained" startIcon={<EditOutlined />}>
          Edit
        </Button>
      </Link>
    </div>
  );
};